import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="variable-input"
//
// A contenteditable-backed text input that supports inserting variable
// "pills" (e.g. {intake_phone}) via button clicks. Users cannot type `{`
// or `}` — the only way to add a variable is by clicking one of the
// insertion buttons. On every edit, the contenteditable contents are
// serialized back into a plain string with `{var_name}` tokens and
// written into a hidden form field.
//
// Targets:
//   - editor: the contenteditable div the user edits
//   - hidden: the hidden input that holds the serialized value ({var} tokens)
//
// Values:
//   - variables: JSON object mapping token keys to human labels
//     e.g. { "intake_phone": "Intake Phone", "intake_email": "Intake Email" }
//
// Actions:
//   - insertVariable: bind to a button click; reads data-variable and
//     data-label from the button and inserts a pill at the cursor.
export default class extends Controller<HTMLElement> {
  static targets = ["editor", "hidden"];
  static values = { variables: Object };

  declare readonly editorTarget: HTMLElement;
  declare readonly hiddenTarget: HTMLInputElement;
  declare readonly variablesValue: Record<string, string>;

  private savedRange: Range | null = null;

  connect() {
    this.renderFromValue(this.hiddenTarget.value || "");
    this.editorTarget.addEventListener(
      "beforeinput",
      this.blockCurlies as EventListener
    );
    this.editorTarget.addEventListener("input", this.serialize);
    this.editorTarget.addEventListener("blur", this.saveSelection);
    this.editorTarget.addEventListener("keydown", this.handleKeydown);
  }

  disconnect() {
    this.editorTarget.removeEventListener(
      "beforeinput",
      this.blockCurlies as EventListener
    );
    this.editorTarget.removeEventListener("input", this.serialize);
    this.editorTarget.removeEventListener("blur", this.saveSelection);
    this.editorTarget.removeEventListener("keydown", this.handleKeydown);
  }

  // Prevent users from typing `{` or `}` manually — the only way to add
  // a variable is through the insertion buttons.
  blockCurlies = (event: InputEvent) => {
    if (!event.data) return;
    if (event.data.includes("{") || event.data.includes("}")) {
      event.preventDefault();
    }
  };

  // Save the selection range whenever the editor loses focus so that
  // clicking a pill button can insert at the previous cursor position.
  saveSelection = () => {
    const selection = window.getSelection();
    if (
      selection &&
      selection.rangeCount > 0 &&
      this.editorTarget.contains(selection.anchorNode)
    ) {
      this.savedRange = selection.getRangeAt(0).cloneRange();
    }
  };

  // Make Backspace delete an entire pill as a single character.
  handleKeydown = (event: KeyboardEvent) => {
    if (event.key !== "Backspace") return;
    const selection = window.getSelection();
    if (!selection || !selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    if (range.startOffset !== 0) return;

    // If the node immediately before the cursor is a pill, remove it.
    const previous =
      range.startContainer.previousSibling ||
      (range.startContainer.nodeType === Node.TEXT_NODE &&
      range.startContainer.textContent === ""
        ? range.startContainer.previousSibling
        : null);
    if (previous && this.isPill(previous as Element)) {
      event.preventDefault();
      (previous as Element).remove();
      this.serialize();
    }
  };

  // Insert a variable pill at the cursor position (or the saved position
  // if focus has left the editor). Called from button click actions.
  insertVariable(event: Event) {
    const button = event.currentTarget as HTMLElement;
    const variable = button.dataset.variable;
    const label = button.dataset.label || variable;
    if (!variable) return;

    this.editorTarget.focus();

    // Restore saved selection if we have one.
    const selection = window.getSelection();
    if (selection && this.savedRange) {
      selection.removeAllRanges();
      selection.addRange(this.savedRange);
    }

    const pill = this.buildPill(variable, label as string);
    const range =
      selection && selection.rangeCount > 0
        ? selection.getRangeAt(0)
        : this.rangeAtEnd();

    // If the cursor is not inside the editor, append at the end instead.
    if (!this.editorTarget.contains(range.startContainer)) {
      this.editorTarget.appendChild(pill);
      this.editorTarget.appendChild(document.createTextNode("\u00a0"));
    } else {
      range.deleteContents();
      range.insertNode(pill);
      // Insert a non-breaking space after the pill so the cursor can sit
      // after it naturally.
      const space = document.createTextNode("\u00a0");
      pill.after(space);
      range.setStartAfter(space);
      range.setEndAfter(space);
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }

    this.serialize();
    this.saveSelection();
  }

  // Walk the editor's child nodes and write a plain-string representation
  // (with `{var}` tokens) into the hidden input.
  serialize = () => {
    let text = "";
    this.editorTarget.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent || "";
      } else if (
        node.nodeType === Node.ELEMENT_NODE &&
        this.isPill(node as Element)
      ) {
        text += `{${(node as HTMLElement).dataset.variable}}`;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        text += (node as HTMLElement).textContent || "";
      }
    });
    this.hiddenTarget.value = text.replace(/\u00a0/g, " ");
  };

  // Initial render: parse the stored string value and build a mix of
  // text nodes and pill elements inside the editor.
  private renderFromValue(value: string) {
    this.editorTarget.innerHTML = "";
    if (!value) return;

    const parts = value.split(/(\{\w+\})/);
    parts.forEach((part) => {
      const match = part.match(/^\{(\w+)\}$/);
      if (match && this.variablesValue[match[1]]) {
        this.editorTarget.appendChild(
          this.buildPill(match[1], this.variablesValue[match[1]])
        );
      } else if (part) {
        this.editorTarget.appendChild(document.createTextNode(part));
      }
    });
  }

  private buildPill(variable: string, label: string): HTMLElement {
    const pill = document.createElement("span");
    pill.contentEditable = "false";
    pill.dataset.variable = variable;
    pill.dataset.pill = "true";
    pill.className = "mention-badge";
    pill.textContent = `{${label}}`;
    return pill;
  }

  private isPill(node: Element): boolean {
    return (
      node.nodeType === Node.ELEMENT_NODE &&
      (node as HTMLElement).dataset?.pill === "true"
    );
  }

  private rangeAtEnd(): Range {
    const range = document.createRange();
    range.selectNodeContents(this.editorTarget);
    range.collapse(false);
    return range;
  }
}
